import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { clearCredentials, setCredentials } from "../../authSlice";
import toast from "react-hot-toast";

function EditSale() {
  const { token } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    zimindarId: "",
    customerId: "",
    item: "",
    weight: "",
    price: "",
    nug: 0,
    bardana: "",
    netPrice: 0,
  });
  const [errors, setErrors] = useState({});
  const [zimidars, setZimidars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedZimindarId, setSelectedZimindarId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tokenExpired, setTokenExpired] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  useEffect(() => {
    const fetchZimidars = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getZimindarofTodayorByDate`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setZimidars(data.payments || []);
        } else {
          throw new Error("Failed to fetch zimidars.");
        }
      } catch (err) {
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchCustomers = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllCustomers`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setCustomers(data.customers || []);
        } else {
          throw new Error("Failed to fetch customers.");
        }
      } catch (err) {
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }

    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
    } else {
      fetchZimidars();
      fetchCustomers();
    }
  }, [dispatch, token, tokenExpired]);

  const handleZimindarChange = (e) => {
    const zimindarId = e.target.value;
    setSelectedZimindarId(zimindarId);
    setFormData((prevFormData) => ({
      ...prevFormData,
      zimindarId: zimindarId,
    }));

    const selectedZimindar = zimidars.find(
      (z) => z.zimindarId._id === zimindarId
    );
    if (selectedZimindar) {
      setItems(selectedZimindar.itemsDescription);
    } else {
      setItems([]); // Reset items if no zimindar found
    }
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.zimindarId) formErrors.zimindarId = "Zimindar is required";
    if (!formData.customerId) formErrors.customer = "Customer is required";
    if (!formData.item) formErrors.item = "Item is required";
    if (!formData.weight) formErrors.weight = "Weight is required";
    if (!formData.price) formErrors.price = "Price is required";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleUpdateSale = async () => {
    if (!validateForm()) return;

    const updatedFormData = {
      ...formData,
      nug: formData.nug === "" ? 0 : formData.nug,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/editSale?id=${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedFormData),
        }
      );

      if (response.status === 401) {
        dispatch(clearCredentials());
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const data = await response.json();
        if (data.status === 200) {
          toast.success("Sale updated successfully", {
            style: {
              border: "1px solid #021F7E",
              padding: "16px",
              color: "#0573E3",
            },
            iconTheme: {
              primary: "#021F7E",
              secondary: "#fff",
            },
            duration: 6000,
          });
          navigate("/dashboard/sales"); // Navigate back to sales list
        } else {
          console.error("Error:", data.message);
        }
      } else {
        const data = await response.json();
        toast.success(data.message);
        console.error("Failed to update sale.");
      }
    } catch (error) {
      console.error("Error updating sale:", error);
    }
  };

  useEffect(() => {
    const fetchSaleById = async () => {
      if (!id) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/sales/getsinglesale?id=${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const saleData = await response.json();
          if (saleData && saleData.status === 200) {
            setFormData({
              zimindarId: saleData.sale.zimindarId || "",
              customerId: saleData.sale.customerId || "",
              item: saleData.sale.item || "",
              weight: saleData.sale.weight || "",
              price: saleData.sale.price || "",
              nug: saleData.sale.nug || 0,
              bardana: saleData.sale.bardana || "",
              netPrice: saleData.sale.netPrice || 0,
            });
            setSelectedZimindarId(saleData.sale.zimindarId || "");

            // Set items based on zimindar selection
            const selectedZimindar = zimidars.find(
              (z) => z.zimindarId._id === saleData.sale.zimindarId
            );
            if (selectedZimindar) {
              setItems(selectedZimindar.itemsDescription);
            } else {
              setItems([]); // Reset items if no zimindar found
            }
          }
        } else {
          console.error("Failed to fetch sales data.");
          navigate("/dashboard/sales");
        }
      } catch (error) {
        console.error("Error fetching sales data:", error);
      }
    };

    if (id) fetchSaleById();
  }, [id, token, navigate, zimidars, dispatch]);

  if (apiError) return <div>Error: {apiError}</div>;

  return (
    <>
      <div className="whiteBgDiv UserManagementPadding">
        Sales <span>Add/Edit Sales entry</span>
        <div className="formHeading UserManagementpage">Stock Sale</div>
        <div className="addSaleGrid">
          <div className="formInputLabelBox">
            <label>Zimindar</label>
            <select
              name="zimindarId"
              className="form-select"
              value={selectedZimindarId}
              onChange={handleZimindarChange}
            >
              <option value="">Select Zimindar</option>
              {zimidars.map((zimidar) => (
                <option
                  key={zimidar.zimindarId._id}
                  value={zimidar.zimindarId._id}
                >
                  {zimidar.zimindarId.name}
                </option>
              ))}
            </select>
            {errors.zimindarId && (
              <span className="errorClass">{errors.zimindarId}</span>
            )}
          </div>
          <div className="formInputLabelBox">
            <label>Customer</label>
            <select
              name="customerId"
              className="form-select"
              value={formData.customerId}
              onChange={handleInputChange}
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer._id} value={customer._id}>
                  {customer.name}
                </option>
              ))}
            </select>
            {errors.customer && (
              <span className="errorClass">{errors.customer}</span>
            )}
          </div>
          <div className="formInputLabelBox">
            <label>Item</label>
            <select
              name="item"
              className="form-select"
              value={formData.item}
              onChange={handleInputChange}
            >
              <option value="">Select Item</option>
              {items.map((item, index) => (
                <option key={index} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {errors.item && <span className="errorClass">{errors.item}</span>}
          </div>
          <div className="formInputLabelBox">
            <label>Weight</label>
            <input
              name="weight"
              type="text"
              placeholder="Enter here"
              value={formData.weight}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) handleInputChange(e);
              }}
            />
            {errors.weight && (
              <span className="errorClass">{errors.weight}</span>
            )}
          </div>
          <div className="formInputLabelBox">
            <label>Price</label>
            <input
              name="price"
              type="text"
              placeholder="Enter here"
              value={formData.price}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) handleInputChange(e);
              }}
            />
            {errors.price && <span className="errorClass">{errors.price}</span>}
          </div>
          <div className="formInputLabelBox">
            <label>Nug</label>
            <input
              name="nug"
              type="text"
              placeholder="Enter here"
              value={formData.nug}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) handleInputChange(e);
              }}
            />
            {errors.nug && <span className="errorClass">{errors.nug}</span>}
          </div>
          <div className="formInputLabelBox">
            <label>Bardana</label>
            <select
              name="bardana"
              className="form-select"
              value={formData.bardana}
              onChange={handleInputChange}
            >
              <option value="">Select Bardana</option>
              {/* {allZimidars.map((zimidar) => (
              <option key={zimidar._id} value={zimidar._id}>
                {zimidar.name}
              </option>
            ))} */}
            </select>
            {errors.bardana && (
              <span className="errorClass">{errors.bardana}</span>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center ">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={formData.netPrice}
              value={formData.netPrice}
              id="NetPrice"
              onChange={(e) => {
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  netPrice: e.target.checked ? 1 : 0,
                }));
              }}
            />
          </div>
          <div className="formInputLabelBox">
            <label htmlFor="NetPrice">Net Price</label>
          </div>
        </div>
        <div className="UserManagementBtnFlexDiv">
          {!loading ? (
            <button className="colorBtn" onClick={handleUpdateSale}>
              Update Sale
            </button>
          ) : (
            <button className="colorBtn">Updating ...</button>
          )}
          <button
            className="grayBtn"
            onClick={() => navigate("/dashboard/sales")}
          >
            Back
          </button>
        </div>
      </div>
    </>
  );
}

export default EditSale;
